import React, { useMemo } from 'react'
import {
  Avatar,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import useIsMobile from '../../../helpers/useIsMobile'
import {
  BUCKET_META,
  BUCKET_ORDER,
  PRIORITY_META,
  STATUS_META,
  getDueMeta,
  getLinkedRecord,
  getTeamColorKey,
  isAssignedToMe,
  resolveColor,
} from './taskHelpers'

const Dot = ({ color }) => (
  <Box
    sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }}
  />
)

// The "List" alternative to the board: one flat list grouped by urgency
const TaskListView = ({ tasks, currentUserId, onOpen }) => {
  const theme = useTheme()
  const isMobile = useIsMobile()

  const groups = useMemo(() => {
    const buckets = Object.fromEntries(BUCKET_ORDER.map((key) => [key, []]))
    tasks.forEach((task) => buckets[getDueMeta(task).bucket].push(task))
    return BUCKET_ORDER.filter((key) => buckets[key].length > 0).map((key) => ({
      key,
      ...BUCKET_META[key],
      tasks: buckets[key],
    }))
  }, [tasks])

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      {groups.map((group) => (
        <Box key={group.key}>
          <Stack
            direction="row"
            alignItems="center"
            gap={1.25}
            sx={{
              height: 38,
              px: 2,
              bgcolor: 'anBackground.main',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: '0.78rem',
                color: resolveColor(theme, group.colorKey),
              }}
            >
              {group.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {group.tasks.length}
            </Typography>
          </Stack>
          {group.tasks.map((task) => {
            const due = getDueMeta(task)
            const linked = getLinkedRecord(task)
            const status = STATUS_META[task.taskStatus]
            return (
              <Stack
                key={task._id}
                direction="row"
                alignItems="center"
                gap={2}
                onClick={() => onOpen(task._id)}
                sx={{
                  minHeight: 58,
                  px: 2,
                  py: 1,
                  cursor: 'pointer',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'anMuted.main' },
                }}
              >
                {!isMobile && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{ width: 86, flexShrink: 0 }}
                  >
                    <Dot
                      color={resolveColor(
                        theme,
                        PRIORITY_META[task.taskPriority].colorKey,
                      )}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {task.taskPriority}
                    </Typography>
                  </Stack>
                )}
                <Box flex={1} minWidth={0}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: 'text.primary' }}
                    noWrap
                  >
                    {task.taskName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {[
                      linked && `${linked.type} · ${linked.label}`,
                      task.createdBy &&
                        `Created by ${task.createdBy.firstName} ${task.createdBy.lastName}`,
                      task.comments?.length > 0 &&
                        `${task.comments.length} comment${
                          task.comments.length === 1 ? '' : 's'
                        }`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </Box>
                {!isMobile && (
                  <>
                    <Box sx={{ width: 150, flexShrink: 0 }}>
                      {task.dueDate && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={due.color}
                          icon={<CalendarTodayIcon />}
                          label={due.label}
                          sx={{
                            borderRadius: 1,
                            ...(due.color === 'default' && { color: 'text.secondary' }),
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ width: 110, flexShrink: 0 }}>
                      <Chip
                        size="small"
                        color={status.colorKey}
                        label={status.label}
                        sx={{ borderRadius: 1 }}
                      />
                    </Box>
                  </>
                )}
                <Box
                  sx={{
                    width: isMobile ? 'auto' : 150,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {task.assignedToUser ? (
                    <>
                      <Avatar
                        src={task.assignedToUser.photograph}
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: 1,
                          fontSize: 11,
                          bgcolor: 'contrast.main',
                        }}
                      >
                        {task.assignedToUser.firstName?.[0]}
                        {task.assignedToUser.lastName?.[0]}
                      </Avatar>
                      {!isMobile && (
                        <Typography variant="caption" color="text.primary" noWrap>
                          {isAssignedToMe(task, currentUserId)
                            ? 'Me'
                            : `${task.assignedToUser.firstName} ${task.assignedToUser.lastName}`}
                        </Typography>
                      )}
                    </>
                  ) : (
                    task.assignedToTeam && (
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={
                          <Dot
                            color={resolveColor(
                              theme,
                              getTeamColorKey(task.assignedToTeam),
                            )}
                          />
                        }
                        label={isMobile ? '' : task.assignedToTeam}
                        sx={{
                          borderRadius: 1,
                          pl: 0.75,
                          color: 'text.secondary',
                          ...(isMobile && { '& .MuiChip-label': { display: 'none' }, pr: 0.5 }),
                        }}
                      />
                    )
                  )}
                </Box>
                <ChevronRightIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </Stack>
            )
          })}
        </Box>
      ))}
    </Paper>
  )
}

export default TaskListView
